import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button, Icon } from '../../../../../components';
import { AFFILIATE_FORM, AFFILIATE_FORM_ANIM, type DeploymentStepState } from '../../../../../theme/tokens';
import { resolveStepMeta } from '../stepMeta';
import { CONFIRM_ID } from '../types';
import type { TimelineEntry } from '../types';
import {
  Backdrop,
  Sheet,
  Header,
  Title,
  CloseBtn,
  ScrollArea,
  List,
  Row,
  Dot,
  RowLabel,
  RowAt,
  RowPill,
  Footer,
} from './DeploymentSummaryModal.styles';

/** Bare host → absolute https URL (mirrors ConfirmationCard / technical.ts). */
const toHref = (domain: string): string => (/^https?:\/\//i.test(domain) ? domain : `https://${domain}`);

/** Settled state → display order weight (done first, then failed, then skipped). */
const ORDER: Record<DeploymentStepState, number> = {
  completed: 0,
  active: 0,
  pending: 0,
  failed: 1,
  skipped: 2,
};

export type DeploymentSummaryModalProps = {
  /** The full replayed timeline (the confirmation entry is filtered out). */
  timeline: readonly TimelineEntry[];
  /** Active brand domain for the footer Visit CTA (disabled when blank). */
  domain?: string;
  onClose: () => void;
};

/**
 * The deployment summary modal — every step that ran (done / failed / skipped),
 * opened from the confirmation card's info CTA. A LOCAL mirror of the casino-settings
 * Overlay shell (portal + Escape + backdrop close + scroll-lock + exit animation),
 * kept local so the home section stays decoupled.
 */
export const DeploymentSummaryModal = ({
  timeline,
  domain,
  onClose,
}: DeploymentSummaryModalProps): React.ReactElement => {
  const { t } = useTranslation();

  const [closing, setClosing] = React.useState(false);
  const closeTimer = React.useRef<number | null>(null);
  const requestClose = React.useCallback(() => {
    setClosing((already) => {
      if (already) return already;
      closeTimer.current = window.setTimeout(onClose, AFFILIATE_FORM_ANIM.SHEET_MS);
      return true;
    });
  }, [onClose]);

  React.useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [requestClose]);

  const onBackdropClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (event.target === event.currentTarget) requestClose();
    },
    [requestClose],
  );

  const trimmed = domain?.trim() ?? '';
  const hasDomain = trimmed.length > 0;
  const onVisit = React.useCallback(() => {
    if (hasDomain) window.open(toHref(trimmed), '_blank', 'noopener,noreferrer');
  }, [hasDomain, trimmed]);

  // Every entry that ran, ordered done → failed → skipped (stable within a group).
  const rows = React.useMemo(
    () =>
      timeline
        .filter((entry) => entry.id !== CONFIRM_ID)
        .map((entry) => ({ entry, state: entry.settledState as DeploymentStepState }))
        .sort((a, b) => ORDER[a.state] - ORDER[b.state]),
    [timeline],
  );

  const pillLabel = (state: DeploymentStepState): string => {
    switch (state) {
      case 'failed':
        return t('Failed');
      case 'skipped':
        return t('Skipped');
      default:
        return t('Done');
    }
  };

  return createPortal(
    <Backdrop onClick={onBackdropClick} $closing={closing}>
      <Sheet role="dialog" aria-modal="true" $closing={closing} aria-label={t('Deployment summary')}>
        <Header>
          <Title text="Deployment summary" tag="span" />
          <CloseBtn type="button" aria-label={t('Close')} onClick={requestClose}>
            <Icon name="close" size={18} color={AFFILIATE_FORM.closeGlyph} />
          </CloseBtn>
        </Header>
        <ScrollArea>
          <List>
            {rows.map(({ entry, state }) => (
              <Row key={entry.id}>
                <Dot $state={state} />
                <RowLabel text={resolveStepMeta(entry.label).title} tag="span" />
                {entry.at ? <RowAt text={entry.at} tag="span" /> : null}
                <RowPill $state={state}>{pillLabel(state)}</RowPill>
              </Row>
            ))}
          </List>
        </ScrollArea>
        <Footer>
          <Button label={t('Visit now')} variant="primary" icon="globe" onClick={onVisit} disabled={!hasDomain} />
        </Footer>
      </Sheet>
    </Backdrop>,
    document.body,
  );
};

export default DeploymentSummaryModal;
